// frontend/src/site/theme/siteStyles.js
// Design System do Microsite (tokens -> classes + estilos)
// Objetivo: presets mudarem "de verdade" (cards, botões, densidade, tipografia)

function cn(...parts) {
  return parts.filter(Boolean).join(' ');
}

/**
 * Densidade (espaçamento) do preset
 * OBS: retornamos também o nome da densidade para uso em botões etc.
 */
function density(theme) {
  const d = theme?.layoutDensity || 'normal';

  const map = {
    airy: {
      name: 'airy',
      pad: 'p-6 md:p-8',
      gap: 'gap-6 md:gap-8',
      sectionY: 'py-16 md:py-20',
      gridGap: 'gap-6 md:gap-8',
      textSm: 'text-[15px] md:text-[16px]',
      controlH: 'h-12',
      controlText: 'text-[15px]',
      pill: 'rounded-[28px]',
    },
    compact: {
      name: 'compact',
      pad: 'p-4 md:p-5',
      gap: 'gap-4 md:gap-5',
      sectionY: 'py-10 md:py-12',
      gridGap: 'gap-4 md:gap-5',
      textSm: 'text-[14px] md:text-[15px]',
      controlH: 'h-10',
      controlText: 'text-sm',
      pill: 'rounded-[22px]',
    },
    normal: {
      name: 'normal',
      pad: 'p-5 md:p-6',
      gap: 'gap-5 md:gap-6',
      sectionY: 'py-12 md:py-16',
      gridGap: 'gap-5 md:gap-6',
      textSm: 'text-[14px] md:text-[16px]',
      controlH: 'h-11',
      controlText: 'text-[15px]',
      pill: 'rounded-[24px]',
    },
  };

  return map[d] || map.normal;
}

/** Aplica tipografia/tokens no container do microsite */
export function siteRootStyle(theme) {
  return {
    backgroundColor: 'var(--bg)',
    color: 'var(--text)',
    fontFamily: theme?.fontBody ? 'var(--fontBody)' : undefined,
  };
}

/** Classes base */
export function textBaseClasses() {
  return cn(
    'text-[color:var(--text)]',
    'selection:bg-[color:var(--accent)] selection:text-[color:var(--accentText)]'
  );
}

export function mutedTextClasses() {
  return 'text-[color:var(--muted)]';
}

export function borderColorClasses() {
  return 'border-[color:var(--border)]';
}

/** Headings: usa tokens de tipografia do preset */
export function headingClasses(theme, size = 'h2') {
  // Peso/Tracking por style inline quando necessário (Tailwind arbitrary é ok, mas é mais seguro assim)
  const w = theme?.headingWeight
    ? `font-[${theme.headingWeight}]`
    : 'font-semibold';

  // Tailwind aceita tracking-[<valor>] mas nem sempre com valores negativos em string do tema
  // então: se vier algo inválido, cai pro tracking-tight
  const ls =
    typeof theme?.letterSpacingHeading === 'string' &&
    theme.letterSpacingHeading.length
      ? `tracking-[${theme.letterSpacingHeading}]`
      : 'tracking-tight';

  const sizeClass = {
    h1: 'text-4xl md:text-6xl',
    h2: 'text-3xl md:text-4xl',
    h3: 'text-2xl md:text-3xl',
    h4: 'text-xl md:text-2xl',
    h5: 'text-lg md:text-xl',
  }[size];

  return cn(
    sizeClass,
    w,
    ls,
    'text-[color:var(--text)]',
    'font-[family:var(--fontHeading)]',
    // line-height do heading pode variar por preset, mas mantemos um default elegante
    'leading-[1.15]'
  );
}

/** Texto de corpo (para parágrafos) */
export function bodyTextClasses(theme, extra = '') {
  const d = density(theme);
  return cn(
    d?.textSm || 'text-[14px] md:text-[16px]',
    'text-[color:var(--text)]',
    extra
  );
}

/** Card: flat | soft | elevated */
export function cardClasses(theme, extra = '') {
  const r = 'rounded-[var(--radius)]';
  const base = cn(
    'bg-[color:var(--card)]',
    r,
    'transition',
    // “ring” leve para dar acabamento premium
    'ring-1 ring-black/5 dark:ring-white/5'
  );

  const style = theme?.cardStyle || 'flat';

  const variant =
    style === 'elevated'
      ? cn(
          'shadow-[0_18px_50px_rgba(0,0,0,0.18)]',
          'border border-[color:rgba(255,255,255,0.06)]',
          'hover:shadow-[0_22px_60px_rgba(0,0,0,0.24)]'
        )
      : style === 'soft'
        ? cn(
            'border border-[color:var(--border)]',
            'shadow-[0_10px_30px_rgba(0,0,0,0.08)]',
            'hover:shadow-[0_14px_38px_rgba(0,0,0,0.10)]'
          )
        : cn('border border-[color:var(--border)]', 'shadow-none');

  return cn(base, variant, extra);
}

/** Wrapper “Glass” (para FilterBar sobre a imagem do hero) */
export function glassClasses(theme, extra = '') {
  // Se estiver em dark, deixa o glass um pouco mais “escuro” e com blur mais forte
  const isDark = (theme?.mode || 'light') === 'dark';

  const base = cn(
    'backdrop-blur',
    isDark
      ? 'bg-[color:rgba(0,0,0,0.35)]'
      : 'bg-[color:rgba(255,255,255,0.75)]',
    'border border-[color:rgba(255,255,255,0.18)]',
    'shadow-[0_18px_60px_-30px_rgba(0,0,0,.45)]'
  );

  // pill externo (mais “capsule”)
  const d = density(theme);
  const pill = d?.pill || 'rounded-[24px]';

  return cn(base, pill, extra);
}

/** Botões: solid | outline | minimal */
export function buttonClasses(theme, variant = 'primary', extra = '') {
  const d = density(theme);

  // Botão com cantos mais “modernos” (menos arredondado que o card)
  const r =
    (theme?.radius ?? 16) >= 22
      ? 'rounded-[18px]'
      : (theme?.radius ?? 16) >= 16
        ? 'rounded-[14px]'
        : 'rounded-[12px]';

  const common = cn(
    r,
    'inline-flex items-center justify-center',
    'font-medium',
    'transition',
    'select-none',
    d?.controlH || 'h-11',
    'px-5',
    d?.controlText || 'text-[15px]',
    'focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--bg)]'
  );

  const style = theme?.buttonStyle || 'outline';

  const primary =
    style === 'solid'
      ? cn(
          'bg-[color:var(--accent)] text-[color:var(--accentText)]',
          'hover:opacity-95'
        )
      : style === 'minimal'
        ? cn(
            'bg-transparent text-[color:var(--text)]',
            'hover:bg-[color:rgba(0,0,0,0.04)]',
            'dark:hover:bg-[color:rgba(255,255,255,0.06)]'
          )
        : cn(
            'bg-transparent text-[color:var(--accent)]',
            'border border-[color:var(--accent)]',
            'hover:bg-[color:rgba(37,99,235,0.08)]',
            'dark:hover:bg-[color:rgba(255,255,255,0.06)]'
          );

  const secondary =
    style === 'solid'
      ? cn(
          'bg-[color:var(--card)] text-[color:var(--text)]',
          'border border-[color:var(--border)]',
          'hover:bg-[color:rgba(255,255,255,0.6)] dark:hover:bg-[color:rgba(255,255,255,0.06)]'
        )
      : style === 'minimal'
        ? cn(
            'bg-transparent text-[color:var(--muted)]',
            'hover:text-[color:var(--text)]'
          )
        : cn(
            'bg-transparent text-[color:var(--text)]',
            'border border-[color:var(--border)]',
            'hover:bg-[color:rgba(0,0,0,0.03)]',
            'dark:hover:bg-[color:rgba(255,255,255,0.05)]'
          );

  const chosen = variant === 'secondary' ? secondary : primary;

  return cn(common, chosen, extra);
}

/** Badge / Chip (neutro) */
export function chipClasses(theme, extra = '') {
  const base = cn(
    'inline-flex items-center gap-2',
    'px-3 py-1',
    'text-xs md:text-sm',
    'rounded-full',
    'border'
  );

  const isDark = (theme?.mode || 'light') === 'dark';

  const chip = isDark
    ? cn(
        'bg-[color:rgba(255,255,255,0.06)]',
        'border-[color:rgba(255,255,255,0.10)]',
        'text-[color:var(--text)]'
      )
    : cn(
        'bg-[color:rgba(15,23,42,0.03)]',
        'border-[color:var(--border)]',
        'text-[color:var(--text)]'
      );

  return cn(base, chip, extra);
}

/** Chip de destaque (usa accent como borda/bolinha, mas sem “pintar tudo”) */
export function chipAccentClasses(theme, extra = '') {
  return chipClasses(
    theme,
    cn(
      'border-[color:rgba(0,0,0,0.08)]',
      'dark:border-[color:rgba(255,255,255,0.12)]',
      extra
    )
  );
}

/** Ícones (stroke vs filled) - (mantém API, mas ajusta para uso real) */
export function iconTone(theme) {
  const s = theme?.iconStyle || 'stroke';
  const commonWrap =
    'bg-[color:rgba(0,0,0,0.02)] dark:bg-[color:rgba(255,255,255,0.06)]';

  return {
    filled: {
      wrap: commonWrap,
      color: 'text-[color:var(--accent)]',
    },
    stroke: {
      wrap: commonWrap,
      color: 'text-[color:var(--accent)]',
    },
  }[s];
}

/** Section spacing (por densidade) */
export function sectionClasses(theme, extra = '') {
  const d = density(theme);
  return cn(d?.sectionY || 'py-12 md:py-16', extra);
}

export function containerClasses(theme, extra = '') {
  return cn('max-w-7xl mx-auto px-6', extra);
}

export function gridGapClasses(theme) {
  const d = density(theme);
  return d?.gridGap || 'gap-5 md:gap-6';
}

/** Input / Select (para FilterBar) */
export function inputClasses(theme, extra = '') {
  const d = density(theme);

  const r =
    (theme?.radius ?? 16) >= 22
      ? 'rounded-[16px]'
      : (theme?.radius ?? 16) >= 16
        ? 'rounded-[14px]'
        : 'rounded-[12px]';

  const base = cn(
    r,
    d?.controlH || 'h-11',
    'w-full',
    'px-4',
    'bg-[color:var(--card)]',
    'text-[color:var(--text)]',
    'border border-[color:var(--border)]',
    'placeholder:text-[color:var(--muted)]',
    'focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]'
  );

  return cn(base, extra);
}

/** Dropdown menu (para sugestões e presets de preço) */
export function menuClasses(theme, extra = '') {
  return cn(
    'rounded-[16px] overflow-hidden shadow-xl',
    'bg-[color:var(--card)] text-[color:var(--text)]',
    'border border-[color:var(--border)]',
    extra
  );
}

export function menuHeaderClasses(theme, extra = '') {
  return cn(
    'px-4 py-1 text-[11px] uppercase tracking-wide',
    mutedTextClasses(),
    extra
  );
}

export function menuItemClasses(theme, extra = '') {
  const isDark = (theme?.mode || 'light') === 'dark';
  return cn(
    'w-full text-left px-4 py-2 transition',
    isDark
      ? 'hover:bg-[color:rgba(255,255,255,0.06)]'
      : 'hover:bg-[color:rgba(0,0,0,0.05)]',
    extra
  );
}

/** Divider */
export function dividerClasses() {
  return 'border-t border-[color:var(--border)]';
}

/** Links “do site” */
export function linkClasses(theme, extra = '') {
  return cn('hover:opacity-90 transition', 'text-[color:var(--accent)]', extra);
}
